import { useEffect, useMemo, useState } from "react";
import api, { money, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Scales } from "@phosphor-icons/react";

const today = () => new Date().toISOString().slice(0, 10);

const CAT_CODE = { prepayment: "PREPAYMENT", "deferred-income": "DEFERRED_INCOME", accrual: "ACCRUAL", depreciation: "DEPRECIATION" };

// delta = expected - xero (= -variance). Mirrors the backend _fix_lines logic for preview.
function fixLines(category, bsCode, bsName, plCode, plName, delta) {
  const mag = Math.round(Math.abs(delta) * 100) / 100;
  const asset = category === "PREPAYMENT";
  const increase = delta > 0;
  const drBs = (asset && increase) || (!asset && !increase);
  return drBs
    ? [{ code: bsCode, name: bsName, debit: mag, credit: 0 },
       { code: plCode, name: plName, debit: 0, credit: mag }]
    : [{ code: plCode, name: plName, debit: mag, credit: 0 },
       { code: bsCode, name: bsName, debit: 0, credit: mag }];
}

export default function FixVarianceModal({ open, onClose, onFixed, row, category }) {
  const [accounts, setAccounts] = useState([]);
  const [plCode, setPlCode] = useState("");
  const [date, setDate] = useState(today());
  const [narration, setNarration] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/accounts").then(({ data }) => setAccounts(data.accounts || [])).catch(() => {});
    setPlCode(row?.suggested_pl_code || "");
    setDate(today());
    setNarration("");
  }, [open, row]);

  const catCode = CAT_CODE[category] || "PREPAYMENT";
  const plClass = catCode === "DEFERRED_INCOME" ? "REVENUE" : "EXPENSE";
  const plCandidates = accounts.filter((a) => a.class === plClass);
  const accName = (code) => accounts.find((a) => a.code === code)?.name || code;

  const delta = useMemo(() => -Math.round((row?.variance || 0) * 100) / 100, [row]);
  const lines = useMemo(() => {
    if (!row || !plCode) return [];
    return fixLines(catCode, row.code, row.account_name, plCode, accName(plCode), delta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row, plCode, delta, category, accounts]);
  const mag = Math.round(Math.abs(delta) * 100) / 100;

  const submit = async () => {
    if (!plCode) { toast.error("Choose a P&L code"); return; }
    setSaving(true);
    try {
      await api.post("/variance/fix", {
        tenant_id: row.tenant_id, code: row.code,
        category: catCode,
        pl_account_code: plCode, variance: row.variance, date, narration: narration.trim(),
      });
      toast.success(`Posted ${money(mag, row.currency)} correcting journal on ${row.code}`);
      onFixed?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not post the correction");
    } finally { setSaving(false); }
  };

  if (!row) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-lg border-slate-200 p-0 gap-0 max-h-[92vh] overflow-y-auto" data-testid="fix-variance-dialog">
        <DialogHeader className="px-7 pt-6 pb-4 border-b border-slate-100">
          <div className="overline text-orangeink mb-1">Fix variance</div>
          <DialogTitle className="font-heading font-bold text-2xl tracking-tighter text-navy">
            True-up {row.code} — {row.account_name}
          </DialogTitle>
          <DialogDescription className="text-navy/70">
            Posts a one-off correcting journal so the Xero ledger matches the register's expected balance.
          </DialogDescription>
        </DialogHeader>

        <div className="px-7 py-6 space-y-5">
          <div className="grid grid-cols-3 gap-3">
            <Cell label="Xero ledger" value={money(row.xero_balance, row.currency)} />
            <Cell label="Expected (register)" value={money(row.expected_balance, row.currency)} />
            <Cell label="Variance" value={money(row.variance, row.currency)} accent />
          </div>

          <div>
            <Lbl>Post correction against P&amp;L code</Lbl>
            <Select value={plCode} onValueChange={setPlCode}>
              <SelectTrigger data-testid="fix-pl-code" className="rounded-lg border-slate-200"><SelectValue placeholder="Select P&L code" /></SelectTrigger>
              <SelectContent>
                {plCandidates.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Lbl>Journal date</Lbl>
            <input type="date" value={date} data-testid="fix-date" onChange={(e) => setDate(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
          </div>

          <div>
            <Lbl>Narration</Lbl>
            <Textarea data-testid="fix-narration" value={narration} onChange={(e) => setNarration(e.target.value)}
              rows={2} className="rounded-lg border-slate-200 focus:border-orange"
              placeholder={`${row.code} variance correction`} />
          </div>

          {lines.length > 0 && (
            <div>
              <div className="overline text-navy/65 mb-2">Correcting journal (posts to Xero on {fmtDate(date)})</div>
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="fix-journal-review">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-navy/[0.04] overline text-navy/65">
                      <th className="text-left px-4 py-2 font-semibold">Account</th>
                      <th className="text-right px-4 py-2 font-semibold">Debit</th>
                      <th className="text-right px-4 py-2 font-semibold">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-4 py-2"><span className="font-mono font-medium text-navy">{l.code}</span> <span className="text-navy/70">{l.name}</span></td>
                        <td className="px-4 py-2 text-right font-mono tabular text-navy">{l.debit ? money(l.debit, row.currency) : "—"}</td>
                        <td className="px-4 py-2 text-right font-mono tabular text-navy">{l.credit ? money(l.credit, row.currency) : "—"}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-slate-200 bg-navy/[0.03] font-semibold">
                      <td className="px-4 py-2 text-navy">Total</td>
                      <td className="px-4 py-2 text-right font-mono tabular text-navy">{money(mag, row.currency)}</td>
                      <td className="px-4 py-2 text-right font-mono tabular text-navy">{money(mag, row.currency)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-7 py-4 border-t border-slate-100 sticky bottom-0 bg-white">
          <button onClick={onClose} data-testid="fix-cancel-btn"
            className="px-5 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">Cancel</button>
          <button onClick={submit} disabled={saving || !plCode} data-testid="fix-submit-btn"
            className="px-6 py-2.5 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors duration-200 disabled:opacity-60 inline-flex items-center gap-2">
            <Scales size={16} />{saving ? "POSTING…" : "Post correction"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Lbl({ children }) { return <span className="overline text-navy/70 block mb-1.5">{children}</span>; }
function Cell({ label, value, accent }) {
  return (
    <div className="bg-navy/[0.03] border border-slate-200 rounded-lg px-3 py-2.5">
      <div className="overline text-navy/65">{label}</div>
      <div className={`font-mono tabular text-base mt-0.5 ${accent ? "text-negative font-semibold" : "text-navy"}`}>{value}</div>
    </div>
  );
}
